import { useDeepMemo } from 'app/hooks/deepMemo';
import { InspectionContext } from 'app/pages/BookInspection/context/InspectionContext';
import { useFetchSampleSize } from 'app/pages/BookInspection/hooks/useFetchSampleSize';
import { useFormikContext } from 'formik';
import React, { useContext, useEffect } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

const CustomAQLSampleSizes = [
  ['customSampleSizeCritical', 'critical.sampleSize'],
  ['customSampleSizeMinor', 'minor.sampleSize'],
  ['customSampleSizeMajor', 'major.sampleSize']
];

const DefectiveAcceptedFieldsNameSuffix = [
  ['customSampleSizeCriticalAcceptance', 'critical.acceptanceLevel'],
  ['customSampleSizeMinorAcceptance', 'minor.acceptanceLevel'],
  ['customSampleSizeMajorAcceptance', 'major.acceptanceLevel']
];

export const InspectionAQLCustomDataInitializer = ({ item, fieldName }) => {
  const formik = useFormikContext();
  const { inspectionLevel } = useContext(InspectionContext);
  const { inspectionSubPOItems, inspectionAQL, isCustomSampling } = item;

  const quantity = React.useMemo(() => {
    return inspectionSubPOItems
      .map(({ orderQty }) => Number(orderQty))
      .filter((orderQty) => !isNaN(orderQty))
      .reduce((a, b) => a + b, 0);
  }, [inspectionSubPOItems]);

  const paramsSampleSize = useDeepMemo(() => {
    return {
      qty: quantity,
      inspectionLevel,
      inspectionAqlCritical: inspectionAQL.criticalLevel,
      inspectionAqlMajor: inspectionAQL.criticalMajorLevel,
      inspectionAqlMinor: inspectionAQL.criticalMinorLevel
    };
  }, [quantity, inspectionLevel, inspectionAQL]);

  const { sampleSize } = useFetchSampleSize(paramsSampleSize, isCustomSampling);

  // Generate custom sample size values
  const generateCustomValues = (fields, source) => {
    return fields.reduce((acc, [key, ref]) => {
      const value = get(source, ref);
      if (value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };
  // Check if fields should be initialized
  const shouldInitializeFields = (fields) => {
    return fields.every(([key]) =>
      [undefined, null, ''].includes(get(item, key))
    );
  };
  const initCustomData = () => {
    const customSampleSize = generateCustomValues(
      CustomAQLSampleSizes,
      sampleSize
    );
    const customDefectiveAccepted = generateCustomValues(
      DefectiveAcceptedFieldsNameSuffix,
      sampleSize
    );

    const shouldInitCustomSampleSize =
      shouldInitializeFields(CustomAQLSampleSizes);
    const shouldInitCustomDefectiveAccepted = shouldInitializeFields(
      DefectiveAcceptedFieldsNameSuffix
    );

    let initCustomValues = {};
    if (shouldInitCustomSampleSize) {
      initCustomValues = { ...initCustomValues, ...customSampleSize };
    }
    if (shouldInitCustomDefectiveAccepted) {
      initCustomValues = { ...initCustomValues, ...customDefectiveAccepted };
    }

    if (!isEmpty(initCustomValues)) {
      setTimeout(() => {
        formik.setFieldValue(fieldName, { ...item, ...initCustomValues });
      }, 250);
    }
  };

  useEffect(() => {
    if (isCustomSampling) {
      initCustomData();
    }
  }, [isCustomSampling, sampleSize]);

  return null;
};
